import { useEffect, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { useAuth, startGoogleLogin } from "@/lib/AuthContext";
import { TID, isOwner } from "@/lib/constants";
import ZeroBSPledge from "@/components/ZeroBSPledge";
import { Link } from "react-router-dom";

const DIGEST_KEY = "nobsgs_silent_digest_enabled";

export default function Settings() {
  const { user, logout } = useAuth();
  const [digest, setDigest] = useState(false);
  const [pledgeOpen, setPledgeOpen] = useState(false);

  useEffect(() => {
    try { setDigest(localStorage.getItem(DIGEST_KEY) === "1"); }
    catch (err) { console.warn("[settings] localStorage read failed:", err?.message || err); }
  }, []);

  const toggleDigest = (v) => {
    setDigest(v);
    try { localStorage.setItem(DIGEST_KEY, v ? "1" : "0"); }
    catch (err) { console.warn("[settings] localStorage write failed:", err?.message || err); }
  };

  return (
    <div className="min-h-screen bg-[#111622]">
      <div className="mx-auto w-full max-w-[760px] px-4 sm:px-6 pt-5" style={{ paddingBottom: "calc(88px + env(safe-area-inset-bottom))" }}>
        <h1 className="text-lg font-semibold text-[#F8FAFC]">Settings</h1>
        <p className="mt-1 text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>Quiet by default. Everything opt-in.</p>

        <div className="mt-5 space-y-3">
          <div className="flex items-center justify-between rounded-2xl bg-[#1A2232] border border-[#26334D] p-4">
            <div className="pr-4">
              <div className="text-sm font-semibold text-[#F8FAFC]">Enable Silent Morning Digest (7:30 AM)</div>
              <p className="mt-1 text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>At most one silent ping when today’s cards are ready. Never afternoon.</p>
            </div>
            <Switch data-testid={TID.settingsDigestToggle} checked={digest} onCheckedChange={toggleDigest} />
          </div>

          <div className="rounded-2xl bg-[#1A2232] border border-[#26334D] p-4">
            <div className="text-sm font-semibold text-[#F8FAFC]">The Zero-BS Pledge</div>
            <p className="mt-1 text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>Review the founder’s contract you accepted.</p>
            <button
              type="button"
              data-testid={TID.pledgeOpen}
              onClick={() => setPledgeOpen(true)}
              className="mt-3 rounded-xl border border-[#26334D] bg-[#1A2232] px-4 py-2 text-sm font-semibold text-[#F8FAFC] hover:bg-[#232E45] transition-colors"
            >Read the Pledge</button>
          </div>

          <div className="rounded-2xl bg-[#1A2232] border border-[#26334D] p-4">
            <div className="text-sm font-semibold text-[#F8FAFC]">Account</div>
            {user ? (
              <>
                <p className="mt-1 text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>Signed in as <span className="font-semibold text-[#F8FAFC]">{user.email}</span></p>
                <button
                  type="button"
                  onClick={logout}
                  data-testid="settings-logout"
                  className="mt-3 rounded-xl border border-[#26334D] bg-[#1A2232] px-4 py-2 text-sm font-semibold text-[#F8FAFC] hover:bg-[#232E45] transition-colors"
                >Sign out</button>
              </>
            ) : (
              <>
                <p className="mt-1 text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>You’re browsing as a guest. Sign in only when needed — nothing else.</p>
                <button
                  type="button"
                  onClick={() => startGoogleLogin({ redirect: "/settings" })}
                  data-testid="settings-login"
                  className="mt-3 rounded-xl bg-[#E85A2A] text-[#F8FAFC] px-4 py-2 text-sm font-semibold hover:bg-[#C94A1D] transition-colors"
                >Sign in with Google</button>
              </>
            )}
          </div>

          {isOwner(user) && (
            <div
              className="rounded-2xl bg-[#1A2232] border border-[#26334D] p-4"
              data-testid="settings-admin-section"
            >
              <div className="text-sm font-semibold text-[#F8FAFC]">Admin</div>
              <p className="mt-1 text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>
                Owner-only. You are signed in as <span className="text-[#F2BD68] font-semibold">{user.email}</span>.
              </p>
              <Link
                to="/studio/login"
                data-testid="settings-open-studio-link"
                className="mt-3 inline-block rounded-xl bg-[#E85A2A] px-4 py-2 text-sm font-semibold text-[#F8FAFC] hover:bg-[#C94A1D] transition-colors shadow-[0_4px_16px_rgba(232,90,42,0.30)]"
              >
                Open Admin Studio
              </Link>
            </div>
          )}

          <div className="rounded-2xl bg-[#1A2232] border border-[#26334D] p-4">
            <div className="text-sm font-semibold text-[#F8FAFC]">About</div>
            <p className="mt-1 text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>
              DISTILL · v1.0.0 · No BS, only GS. Built by an aspirant, for aspirants. No ads. No mid-day panic. No upsells.
            </p>
            <div className="mt-3 pt-3 border-t border-[#26334D]">
              <Link
                to="/privacy"
                data-testid="settings-privacy-link"
                className="inline-flex items-center gap-1 text-xs font-semibold text-[#F2BD68] hover:text-[#E85A2A] underline decoration-[#26334D] underline-offset-4 transition-colors"
              >
                Privacy Policy
              </Link>
              <span className="mx-2 text-[#26334D]">·</span>
              <a
                href="mailto:distillgs@gmail.com"
                className="text-xs font-semibold text-[#94A3B8] hover:text-[#F8FAFC] transition-colors"
              >
                distillgs@gmail.com
              </a>
            </div>
          </div>
        </div>
      </div>

      <ZeroBSPledge
        open={pledgeOpen}
        onAccept={() => setPledgeOpen(false)}
        onClose={() => setPledgeOpen(false)}
      />
    </div>
  );
}
